import {
  ArrowLeftEndOnRectangleIcon,
  FireIcon,
  Squares2X2Icon,
  SunIcon
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { GridSpaceDataType, LevelType } from 'data/types';
import { useLayoutEffect, useRef, useState } from 'react';
import {
  getAllAdjacentSpaces,
  getOpenToBorderRadiusClass,
  getShouldShowLabel
} from 'utils/grid';
import {
  getGridSpaceOutlineVertices,
  shouldUseOpenToOutline
} from 'utils/open-to-outline';
import { getHiddenIntervalsPerEdgeForGridSpace } from 'utils/polygon-shared-edges';
import {
  getCenteredLabelPosition,
  getSectionLabelGroupForSpace,
  getSectionLabelGroups
} from 'utils/section-labels';
import {
  NEW_SPACE_PREVIEW_HEIGHT_PX,
  NEW_SPACE_PREVIEW_WIDTH_PX,
  resolveSectionDragPreviewLabel,
  resolveSpaceDragPreviewLabel
} from 'utils/space-label-drag-preview';
import {
  isPolygonGridSpace,
  scaleVertices,
  verticesToCssPolygon
} from 'utils/space-polygon';
import { findSpaceById } from 'utils/structure';
import DragOverlayItem, {
  IconDragOverlayItem,
  LabeledDragOverlayItem
} from './drag-overlay-item';
import GridSpaceOutlineSvg from './grid-space-outline-svg';
import OpenFloorPlanWalls from './open-floor-plan-walls';
import {
  SectionDragPreviewLabelContent,
  SpaceDragPreviewLabel
} from './space-drag-preview-label';

type SingleGridSpaceOverlayProps = {
  space: GridSpaceDataType;
  showZones: boolean;
  zoom?: number;
};

export function SingleGridSpaceOverlay({
  space,
  showZones,
  zoom = 1
}: SingleGridSpaceOverlayProps) {
  const hasSectionColor = showZones && space.sectionColor;
  const isPolygon = isPolygonGridSpace(
    space.vertices,
    space.position.width,
    space.position.height
  );
  const preview = resolveSpaceDragPreviewLabel({
    label: space.label,
    widthPx: space.position.width,
    heightPx: space.position.height,
    zoom,
    vertices: space.vertices
  });
  const label = (
    <SpaceDragPreviewLabel
      preview={preview}
      zoom={zoom}
      hasSectionColor={!!hasSectionColor}
      sectionColor={space.sectionColor}
    />
  );

  if (isPolygon && space.vertices) {
    const clipPath = verticesToCssPolygon(scaleVertices(space.vertices, zoom));
    const backgroundColor =
      hasSectionColor && space.sectionColor ? space.sectionColor.bg : '#ffffff';
    const stroke =
      hasSectionColor && space.sectionColor
        ? space.sectionColor.border
        : '#9ca3af';
    return (
      <div
        style={{
          position: 'relative',
          width: space.position.width * zoom,
          height: space.position.height * zoom,
          opacity: 0.8,
          cursor: 'grabbing'
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor,
            clipPath,
            WebkitClipPath: clipPath
          }}
        >
          <GridSpaceOutlineSvg
            vertices={space.vertices}
            bboxWidth={space.position.width}
            bboxHeight={space.position.height}
            stroke={stroke}
          />
        </div>
        {label}
      </div>
    );
  }

  return (
    <DragOverlayItem
      width={space.position.width * zoom}
      height={space.position.height * zoom}
      colorScheme={hasSectionColor ? 'custom' : 'white'}
      customColors={
        hasSectionColor && space.sectionColor
          ? { bg: space.sectionColor.bg, border: space.sectionColor.border }
          : undefined
      }
    >
      <div className='relative h-full w-full'>{label}</div>
    </DragOverlayItem>
  );
}

type MultiSpaceItemProps = {
  space: GridSpaceDataType;
  structure: LevelType[];
  allSpaces: GridSpaceDataType[];
  minX: number;
  minY: number;
  easyMode: boolean;
  showZones: boolean;
  zoom?: number;
  suppressInternalLabel?: boolean;
};

export function MultiSpaceItem({
  space,
  structure,
  allSpaces,
  minX,
  minY,
  easyMode,
  showZones,
  zoom = 1,
  suppressInternalLabel = false
}: MultiSpaceItemProps) {
  const adjacentSpaces = getAllAdjacentSpaces(space, allSpaces);
  const currentSpace = findSpaceById(structure, space.spaceId);
  const hasOpenTo = (currentSpace?.openTo?.length ?? 0) > 0;
  const borderRadiusClass = getOpenToBorderRadiusClass(
    space,
    allSpaces,
    structure
  );
  const shouldShowLabel = getShouldShowLabel(
    space,
    allSpaces,
    structure,
    hasOpenTo
  );
  const hasSectionColor = showZones && space.sectionColor;
  const isPolygon = isPolygonGridSpace(
    space.vertices,
    space.position.width,
    space.position.height
  );
  const useOpenToOutline = shouldUseOpenToOutline(
    isPolygon,
    currentSpace?.openTo,
    easyMode
  );
  const outlineVertices = getGridSpaceOutlineVertices(space);
  const polygonClipPath =
    isPolygon && space.vertices
      ? verticesToCssPolygon(scaleVertices(space.vertices, zoom))
      : undefined;
  const polygonStroke =
    hasSectionColor && space.sectionColor
      ? space.sectionColor.border
      : '#9ca3af';
  const polygonHiddenIntervalsPerEdge = useOpenToOutline
    ? getHiddenIntervalsPerEdgeForGridSpace(space, allSpaces, currentSpace)
    : undefined;
  const preview = resolveSpaceDragPreviewLabel({
    label: space.label,
    widthPx: space.position.width,
    heightPx: space.position.height,
    zoom,
    vertices: space.vertices
  });

  const labelContent = shouldShowLabel && !suppressInternalLabel && (
    <SpaceDragPreviewLabel
      preview={preview}
      zoom={zoom}
      hasSectionColor={!!hasSectionColor}
      sectionColor={space.sectionColor}
    />
  );

  return (
    <div
      className='absolute'
      style={{
        left: (space.position.x - minX) * zoom,
        top: (space.position.y - minY) * zoom,
        width: space.position.width * zoom,
        height: space.position.height * zoom
      }}
    >
      <div
        className={clsx(
          'absolute inset-0 shadow-lg',
          !useOpenToOutline && 'border-2',
          !useOpenToOutline && !hasSectionColor && 'border-gray-400',
          !hasSectionColor && 'bg-white',
          !useOpenToOutline && borderRadiusClass
        )}
        style={{
          ...(hasSectionColor && space.sectionColor
            ? {
                backgroundColor: space.sectionColor.bg,
                borderColor: space.sectionColor.border
              }
            : {}),
          ...(polygonClipPath
            ? { clipPath: polygonClipPath, WebkitClipPath: polygonClipPath }
            : {})
        }}
      >
        {useOpenToOutline && (
          <GridSpaceOutlineSvg
            vertices={outlineVertices}
            bboxWidth={space.position.width}
            bboxHeight={space.position.height}
            stroke={polygonStroke}
            hiddenIntervalsPerEdge={polygonHiddenIntervalsPerEdge}
          />
        )}
        {!useOpenToOutline && (
          <OpenFloorPlanWalls
            space={space}
            adjacentSpaces={adjacentSpaces}
            structure={structure}
            hasSectionColor={!!hasSectionColor}
          />
        )}
      </div>
      {labelContent}
    </div>
  );
}

type MultiSpaceOverlayProps = {
  structure: LevelType[];
  draggedSectionSpaces: GridSpaceDataType[];
  activeDragData: { space?: GridSpaceDataType };
  easyMode: boolean;
  showZones: boolean;
  zoom?: number;
};

export function MultiSpaceOverlay({
  structure,
  draggedSectionSpaces,
  activeDragData,
  easyMode,
  showZones,
  zoom = 1
}: MultiSpaceOverlayProps) {
  const SECTION_LABEL_PADDING = 8;
  const minX = Math.min(...draggedSectionSpaces.map((s) => s.position.x));
  const minY = Math.min(...draggedSectionSpaces.map((s) => s.position.y));
  const maxX = Math.max(
    ...draggedSectionSpaces.map((s) => s.position.x + s.position.width)
  );
  const maxY = Math.max(
    ...draggedSectionSpaces.map((s) => s.position.y + s.position.height)
  );

  const draggedSpace = draggedSectionSpaces.find(
    (s) => s.id === activeDragData.space?.id
  );
  const offsetX = draggedSpace ? draggedSpace.position.x - minX : 0;
  const offsetY = draggedSpace ? draggedSpace.position.y - minY : 0;
  const sectionLabelGroups = getSectionLabelGroups(
    draggedSectionSpaces,
    structure
  );
  const sectionLabelEntries = Array.from(sectionLabelGroups.values());
  const labelMeasureRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [labelSizes, setLabelSizes] = useState<
    Record<string, { width: number; height: number }>
  >({});

  useLayoutEffect(() => {
    const nextSizes: Record<string, { width: number; height: number }> = {};
    sectionLabelEntries.forEach((group) => {
      const node = labelMeasureRefs.current.get(group.key);
      if (!node) {
        return;
      }
      const rect = node.getBoundingClientRect();
      nextSizes[group.key] = {
        width: rect.width / zoom,
        height: rect.height / zoom
      };
    });
    setLabelSizes((currentSizes) => {
      const currentKeys = Object.keys(currentSizes).sort();
      const nextKeys = Object.keys(nextSizes).sort();
      if (
        currentKeys.length !== nextKeys.length ||
        currentKeys.some((key, index) => key !== nextKeys[index])
      ) {
        return nextSizes;
      }
      const changed = nextKeys.some((key) => {
        const current = currentSizes[key];
        const next = nextSizes[key];
        return (
          !current ||
          Math.abs(current.width - next.width) > 0.5 ||
          Math.abs(current.height - next.height) > 0.5
        );
      });
      return changed ? nextSizes : currentSizes;
    });
  }, [sectionLabelEntries, zoom]);

  const sectionLabelPreviews = sectionLabelEntries.map((group) => ({
    group,
    preview: resolveSectionDragPreviewLabel({
      label: group.label,
      boundsWidth: group.bounds.width,
      boundsHeight: group.bounds.height,
      zoom
    })
  }));

  return (
    <div
      style={{
        position: 'relative',
        width: (maxX - minX) * zoom,
        height: (maxY - minY) * zoom,
        opacity: 0.8,
        transform: `translate(-${offsetX * zoom}px, -${offsetY * zoom}px)`,
        cursor: 'grabbing'
      }}
    >
      {draggedSectionSpaces.map((space) => (
        <MultiSpaceItem
          key={space.id}
          space={space}
          structure={structure}
          allSpaces={draggedSectionSpaces}
          minX={minX}
          minY={minY}
          easyMode={easyMode}
          showZones={showZones}
          zoom={zoom}
          suppressInternalLabel={
            !!getSectionLabelGroupForSpace(space, sectionLabelGroups)
          }
        />
      ))}
      {sectionLabelPreviews.map(({ group, preview }) => {
        const centeredPosition = getCenteredLabelPosition(
          group,
          labelSizes[group.key],
          { padding: SECTION_LABEL_PADDING }
        );
        return (
          <div
            key={`overlay-center-label-${group.key}`}
            className='pointer-events-none absolute z-[20] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center text-center text-sm font-medium'
            style={{
              left: (centeredPosition.x - minX) * zoom,
              top: (centeredPosition.y - minY) * zoom
            }}
          >
            <SectionDragPreviewLabelContent
              displayLabel={preview.displayLabel}
            />
          </div>
        );
      })}
      {sectionLabelPreviews.map(({ group, preview }) => (
        <div
          key={`overlay-center-label-measure-${group.key}`}
          ref={(node) => {
            if (node) {
              labelMeasureRefs.current.set(group.key, node);
            } else {
              labelMeasureRefs.current.delete(group.key);
            }
          }}
          className='pointer-events-none absolute left-0 top-0 z-[-1] flex select-none flex-col items-center justify-center text-center text-sm font-medium opacity-0'
        >
          <SectionDragPreviewLabelContent displayLabel={preview.displayLabel} />
        </div>
      ))}
    </div>
  );
}

type DraggedItemType = { id: string; label: string; type: string };

type DraggedItemOverlayProps = {
  draggedItem: DraggedItemType;
  zoom?: number;
};

export function DraggedItemOverlay({
  draggedItem,
  zoom = 1
}: DraggedItemOverlayProps) {
  if (draggedItem.type === 'space') {
    const preview = resolveSpaceDragPreviewLabel({
      label: draggedItem.label,
      widthPx: NEW_SPACE_PREVIEW_WIDTH_PX,
      heightPx: NEW_SPACE_PREVIEW_HEIGHT_PX,
      zoom
    });
    return (
      <DragOverlayItem
        width={NEW_SPACE_PREVIEW_WIDTH_PX * zoom}
        height={NEW_SPACE_PREVIEW_HEIGHT_PX * zoom}
        colorScheme='white'
      >
        <div className='relative h-full w-full'>
          <SpaceDragPreviewLabel
            preview={preview}
            zoom={zoom}
            hasSectionColor={false}
          />
        </div>
      </DragOverlayItem>
    );
  }

  if (
    draggedItem.type === 'condenser' ||
    draggedItem.type === 'condenser-ground' ||
    draggedItem.type === 'condenser-wall' ||
    draggedItem.type === 'condenser-roof'
  ) {
    return (
      <LabeledDragOverlayItem
        width={30 * zoom}
        height={30 * zoom}
        colorScheme='purple'
        label={draggedItem.label}
        zoom={zoom}
      />
    );
  }

  if (
    draggedItem.type === 'air-handler' ||
    draggedItem.type === 'air-handler-high-wall' ||
    draggedItem.type === 'air-handler-low-wall' ||
    draggedItem.type === 'air-handler-floor-ceiling' ||
    draggedItem.type === 'air-handler-ceiling-cassette' ||
    draggedItem.type === 'air-handler-ceiling-cassette-slim-fit' ||
    draggedItem.type === 'air-handler-concealed-duct' ||
    draggedItem.type === 'air-handler-ducted'
  ) {
    return (
      <LabeledDragOverlayItem
        width={30 * zoom}
        height={30 * zoom}
        colorScheme='green'
        label={draggedItem.label}
        zoom={zoom}
      />
    );
  }

  if (draggedItem.type === 'electrical-panel') {
    return (
      <LabeledDragOverlayItem
        width={30 * zoom}
        height={30 * zoom}
        colorScheme='yellow'
        label={draggedItem.label}
        zoom={zoom}
      />
    );
  }

  if (draggedItem.type === 'existing-condenser') {
    return (
      <LabeledDragOverlayItem
        width={30 * zoom}
        height={30 * zoom}
        colorScheme='purple'
        label={draggedItem.label}
        zoom={zoom}
      />
    );
  }

  if (draggedItem.type === 'existing-furnace') {
    return (
      <LabeledDragOverlayItem
        width={30 * zoom}
        height={30 * zoom}
        colorScheme='orange'
        label={draggedItem.label}
        zoom={zoom}
      />
    );
  }

  if (
    draggedItem.type === 'window-small' ||
    draggedItem.type === 'window-medium' ||
    draggedItem.type === 'window-large' ||
    draggedItem.type === 'door-french' ||
    draggedItem.type === 'door-double-glass' ||
    draggedItem.type === 'door-glass-single' ||
    draggedItem.type === 'door-glass-double'
  ) {
    return (
      <IconDragOverlayItem
        width={40}
        height={40}
        colorScheme='cyan'
        icon={<Squares2X2Icon className='h-8 w-8 text-cyan-900' />}
      />
    );
  }

  if (
    draggedItem.type === 'door-wood' ||
    draggedItem.type === 'door-metal' ||
    draggedItem.type === 'door-front' ||
    draggedItem.type === 'door-pet' ||
    draggedItem.type === 'door-garage-single' ||
    draggedItem.type === 'door-garage-double'
  ) {
    return (
      <IconDragOverlayItem
        width={40}
        height={40}
        colorScheme='amber'
        icon={
          <ArrowLeftEndOnRectangleIcon className='h-8 w-8 text-amber-900' />
        }
      />
    );
  }

  if (draggedItem.type === 'appliance') {
    return (
      <IconDragOverlayItem
        width={40}
        height={40}
        colorScheme='purple'
        icon={<FireIcon className='h-6 w-6 text-purple-900' />}
      />
    );
  }

  if (
    draggedItem.type === 'skylight-small' ||
    draggedItem.type === 'skylight-medium' ||
    draggedItem.type === 'skylight-large' ||
    draggedItem.type === 'grid-skylight'
  ) {
    const skylightSizes: Record<string, { width: number; height: number }> = {
      'skylight-small': { width: 30, height: 30 },
      'skylight-medium': { width: 40, height: 40 },
      'skylight-large': { width: 50, height: 50 },
      'grid-skylight': { width: 40, height: 40 }
    };
    const size = skylightSizes[draggedItem.type] ?? { width: 40, height: 40 };

    return (
      <IconDragOverlayItem
        width={size.width}
        height={size.height}
        colorScheme='yellow'
        icon={<SunIcon className='h-6 w-6 text-yellow-900' />}
      />
    );
  }

  return null;
}
